import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SessionPlayer {
  player_id: string;
  buy_ins: number;
  players: { name: string; nickname: string | null } | null;
}

interface Session {
  id: string;
  date: string;
  status: string;
  buy_in_unit: number;
  created_at: string;
}

export default function LiveSession() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<Session | null>(null);
  const [players, setPlayers] = useState<SessionPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsSince, setSecondsSince] = useState(0);

  const fetchData = useCallback(async () => {
    if (!sessionId) return;
    setRefreshing(true);

    try {
      const { data: sessionData, error } = await supabase
        .from('sessions')
        .select('id, date, status, buy_in_unit, created_at')
        .eq('id', sessionId)
        .single();

      if (error || !sessionData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const { data: playerData } = await supabase
        .from('session_players')
        .select('player_id, buy_ins, players(name, nickname)')
        .eq('session_id', sessionId);

      setSession(sessionData as Session);
      setPlayers((playerData as unknown as SessionPlayer[]) || []);
      setLastUpdated(new Date());
      setSecondsSince(0);
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, [sessionId]);

  // Initial load + Realtime subscription + 30s polling fallback
  useEffect(() => {
    if (!sessionId) return;
    fetchData();

    const channel = supabase
      .channel(`live-view-${sessionId}`)
      .on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: 'session_players', filter: `session_id=eq.${sessionId}` },
        () => { fetchData(); }
      )
      .subscribe();

    const poll = setInterval(fetchData, 30_000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [sessionId, fetchData]);

  // Tick "X seconds ago"
  useEffect(() => {
    const timer = setInterval(() => setSecondsSince(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, [lastUpdated]);

  const buyInUnit = session?.buy_in_unit ?? 20;
  const sorted = [...players].sort((a, b) => b.buy_ins - a.buy_ins);
  const totalPot = sorted.reduce((s, p) => s + p.buy_ins * buyInUnit, 0);
  const isLive = session?.status === 'unsettled';

  /* ── Shared inline styles ──────────────────────────── */
  const bg = '#0f0f13';
  const surface = '#18182a';
  const border = '#2a2a42';
  const muted = '#6b7280';
  const purple = '#8b5cf6';
  const text = '#e2e8f0';

  if (loading) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, fontFamily: 'system-ui,sans-serif' }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${purple}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '0 auto 1rem' }} />
          <p style={{ color: muted, fontSize: '0.875rem' }}>Loading live session…</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, fontFamily: 'system-ui,sans-serif', padding: '1.5rem' }}>
        <div style={{ textAlign: 'center', color: text }}>
          <p style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔍</p>
          <p style={{ fontWeight: 700, fontSize: '1.125rem', marginBottom: '0.5rem' }}>Session not found</p>
          <p style={{ color: muted, fontSize: '0.875rem' }}>This link may be invalid or the session has been deleted.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100dvh', 
      background: `radial-gradient(circle at 50% 0%, #1e1e2d 0%, ${bg} 100%)`, 
      color: text, 
      fontFamily: 'system-ui,sans-serif', 
      padding: '1.25rem', 
      maxWidth: 480, 
      margin: '0 auto',
      position: 'relative'
    }}>
      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        @keyframes pulse-soft { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.02); } }
        @keyframes fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ── Header ──────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <p style={{ fontSize: '0.625rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: purple, margin: 0 }}>SSC Score Tracker</p>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 800, margin: '0.125rem 0 0', color: '#f1f5f9', lineHeight: 1 }}>Live Session</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button 
            onClick={() => fetchData()} 
            disabled={refreshing}
            style={{ 
              background: surface, 
              border: `1px solid ${border}`, 
              padding: '0.5rem', 
              borderRadius: '0.5rem',
              color: refreshing ? purple : muted, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </button>
          {isLive ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: '#dc2626', padding: '0.375rem 0.875rem', borderRadius: 9999, boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'pulse 1.2s ease-in-out infinite', flexShrink: 0 }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#fff', letterSpacing: '0.06em' }}>LIVE</span>
            </div>
          ) : (
            <div style={{ background: '#374151', padding: '0.375rem 0.875rem', borderRadius: 9999 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af' }}>ENDED</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Player list ─────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.875rem', marginBottom: '1.5rem' }}>
        {(() => {
          const buyIns = players.map(p => p.buy_ins);
          const maxBuyIns = buyIns.length > 0 ? Math.max(...buyIns) : 0;
          const minBuyIns = buyIns.length > 0 ? Math.min(...buyIns) : 0;
          const hasVariation = maxBuyIns !== minBuyIns;

          return sorted.map((sp, i) => {
            const isHighest = hasVariation && sp.buy_ins === maxBuyIns;
            const isLowest = hasVariation && sp.buy_ins === minBuyIns;
            
            let cardBg = surface;
            let cardBorder = border;
            let numColor = '#f1f5f9';
            let labelColor = muted;

            if (isHighest) {
              cardBg = 'linear-gradient(135deg, #450a0a 0%, #18182a 100%)';
              cardBorder = 'rgba(239, 68, 68, 0.4)';
              numColor = '#fca5a5';
              labelColor = 'rgba(239, 68, 68, 0.6)';
            } else if (isLowest) {
              cardBg = 'linear-gradient(135deg, #064e3b 0%, #18182a 100%)';
              cardBorder = 'rgba(16, 185, 129, 0.4)';
              numColor = '#6ee7b7';
              labelColor = 'rgba(16, 185, 129, 0.6)';
            }

            return (
              <div
                key={sp.player_id}
                style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  borderRadius: '1.25rem',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  minWidth: 0,
                  boxShadow: isHighest ? '0 10px 25px -5px rgba(239, 68, 68, 0.3), inset 0 0 20px rgba(239, 68, 68, 0.05)' : isLowest ? '0 10px 25px -5px rgba(16, 185, 129, 0.3), inset 0 0 20px rgba(16, 185, 129, 0.05)' : '0 4px 16px rgba(0,0,0,0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  animation: `fade-in 0.4s ease-out backwards ${i * 0.05}s`
                }}
              >
                {isHighest && <div style={{ position: 'absolute', top: 0, right: 0, padding: '0.25rem 0.5rem', background: '#dc2626', color: '#fff', fontSize: '0.5rem', fontWeight: 900, borderBottomLeftRadius: '0.5rem', letterSpacing: '0.05em' }}>WHALE</div>}
                {isLowest && <div style={{ position: 'absolute', top: 0, right: 0, padding: '0.25rem 0.5rem', background: '#059669', color: '#fff', fontSize: '0.5rem', fontWeight: 900, borderBottomLeftRadius: '0.5rem', letterSpacing: '0.05em' }}>PRO</div>}
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: isHighest ? '#dc2626' : isLowest ? '#059669' : '#374151',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', fontWeight: 900,
                    color: '#fff',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ 
                      fontWeight: 800, 
                      fontSize: '0.9375rem', 
                      margin: 0, 
                      lineHeight: 1.2,
                      color: '#f8fafc',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      letterSpacing: '-0.01em'
                    }}>
                      {sp.players?.name ?? 'Unknown'}
                    </p>
                  </div>
                </div>

                <div style={{ 
                  background: 'rgba(0,0,0,0.3)', 
                  borderRadius: '0.875rem', 
                  padding: '0.75rem', 
                  textAlign: 'center',
                  border: `1px solid ${isHighest ? 'rgba(239, 68, 68, 0.2)' : isLowest ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255,255,255,0.05)'}`
                }}>
                  <p style={{ fontWeight: 900, fontSize: '2.5rem', color: numColor, margin: 0, lineHeight: 1, letterSpacing: '-0.04em' }}>
                    {sp.buy_ins}
                  </p>
                  <p style={{ fontSize: '0.625rem', color: labelColor, textTransform: 'uppercase', fontWeight: 900, letterSpacing: '0.1em', margin: '0.375rem 0 0' }}>
                    Buy-ins
                  </p>
                </div>

                {sp.players?.nickname && (
                  <p style={{ 
                    fontSize: '0.625rem', 
                    color: muted, 
                    margin: 0, 
                    textAlign: 'center',
                    opacity: 0.8,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    "{sp.players.nickname}"
                  </p>
                )}
              </div>
            );
          });
        })()}

        {players.length === 0 && (
          <p style={{ gridColumn: '1 / span 2', textAlign: 'center', color: muted, fontSize: '0.875rem', padding: '3rem 0' }}>
            No players joined yet
          </p>
        )}
      </div>

      {/* ── Total pot hero ──────────────────────── */}
      <div style={{ 
        background: 'linear-gradient(135deg, #6366f1, #a855f7)', 
        borderRadius: '1.25rem', 
        padding: '1.75rem 1.5rem', 
        marginBottom: '1rem', 
        textAlign: 'center',
        boxShadow: '0 12px 30px -10px rgba(99, 102, 241, 0.5)',
        border: '1px solid rgba(255,255,255,0.15)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* subtle glow effect */}
        <div style={{ position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        
        <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,.8)', textTransform: 'uppercase', fontWeight: 900, letterSpacing: '0.15em', margin: 0, position: 'relative' }}>Total Pot Value</p>
        <p style={{ 
          fontSize: '3.25rem', 
          fontWeight: 900, 
          color: '#fff', 
          fontVariantNumeric: 'tabular-nums', 
          lineHeight: 1, 
          margin: '0.5rem 0 0.75rem', 
          letterSpacing: '-0.04em', 
          position: 'relative', 
          textShadow: '0 0 20px rgba(255,255,255,0.3), 0 0 40px rgba(168, 85, 247, 0.4)',
          animation: isLive ? 'pulse-soft 3s infinite ease-in-out' : 'none'
        }}>
          {totalPot.toLocaleString('en-IN')}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', position: 'relative' }}>
          <span style={{ height: 1, flex: 1, background: 'rgba(255,255,255,0.2)' }} />
          <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'rgba(255,255,255,.9)', margin: 0 }}>
            {players.length} Players · {players.reduce((s, p) => s + p.buy_ins, 0)} Buy-ins
          </p>
          <span style={{ height: 1, flex: 1, background: 'rgba(255,255,255,0.2)' }} />
        </div>
      </div>

      {/* ── Session meta ────────────────────────── */}
      <div style={{ 
        background: surface, 
        border: `1px solid ${border}`, 
        borderRadius: '1.25rem', 
        padding: '1.25rem', 
        marginBottom: '1.5rem', 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '1rem',
        boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
      }}>
        <div>
          <p style={{ fontSize: '0.625rem', color: muted, textTransform: 'uppercase', fontWeight: 900, letterSpacing: '0.1em', margin: 0 }}>Session Date</p>
          <p style={{ fontSize: '1rem', fontWeight: 800, color: '#f1f5f9', marginTop: '0.375rem', margin: '0.375rem 0 0', letterSpacing: '-0.01em' }}>
            {session?.date
              ? new Date(session.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
              : '—'}
          </p>
        </div>
        <div>
          <p style={{ fontSize: '0.625rem', color: muted, textTransform: 'uppercase', fontWeight: 900, letterSpacing: '0.1em', margin: 0 }}>Buy-in Unit</p>
          <p style={{ fontSize: '1rem', fontWeight: 800, color: '#f1f5f9', marginTop: '0.375rem', margin: '0.375rem 0 0', letterSpacing: '-0.01em' }}>{buyInUnit}</p>
        </div>
      </div>

      {/* ── Footer ──────────────────────────────── */}
      {lastUpdated && (
        <p style={{ textAlign: 'center', fontSize: '0.6875rem', color: '#4b5563', userSelect: 'none' }}>
          {isLive ? '🔴 Live' : '⏹ Session ended'}
          {' · '}
          {secondsSince < 5
            ? 'updated just now'
            : `updated ${secondsSince}s ago`}
        </p>
      )}
    </div>
  );
}
